import { useEffect, useState, useRef } from "react";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { DataTable, DataTableRowEditCompleteEvent } from "primereact/datatable";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { useTranslation } from "react-i18next";
import RawMaterialService from "../../../services/RawMaterials/RawMaterialService";

interface Props {
  rawMaterials: any[];
  setRawMaterials: (data: any[]) => void;
}

const RawMaterialDatatable: React.FC<Props> = ({ rawMaterials, setRawMaterials }) => {
  const { t } = useTranslation();
  const [materials, setMaterials] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [measurementTypes, setMeasurementTypes] = useState<any[]>([]);
  const toast = useRef<Toast>(null);
  const [editingRows, setEditingRows] = useState<{ [key: number]: boolean }>({});

  useEffect(() => {
    setMaterials(rawMaterials || []);
  }, [rawMaterials]);

  // Fetch dropdown data
  useEffect(() => {
    const fetchDropdownData = async () => {
      const categoriesResponse = await RawMaterialService.RMCategories();
      const measurementTypesResponse = await RawMaterialService.RMMeasurementtypes();

      if (categoriesResponse) {
        setCategories(
          categoriesResponse.map((c: any) => ({ label: c.categoryName, value: c.categoryID }))
        );
      }

      if (measurementTypesResponse) {
        setMeasurementTypes(
          measurementTypesResponse.map((m: any) => ({ label: m.measurementTypeName, value: m.measurementTypeID }))
        );
      }
    };
    fetchDropdownData();
  }, []);

  const showMessage = (label: string, message: string, severity: any) => {
    toast.current?.show({ severity, summary: label, detail: message, life: 3000 });
  };

  // Text Editor with validation for HSN Code
  const textEditor = (options: any) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      let value = e.target.value;

      if (options.field === "hsnCode") {
        const regex = /^[A-Za-z0-9]*$/;
        if (!regex.test(value)) return; 
      }

      options.editorCallback!(value);
    };

    return <InputText value={options.value} onChange={handleChange} style={{ width: "95%" }} />;
  };

  const dropdownEditor = (options: any, data: any[]) => (
    <Dropdown
      value={options.value}
      options={data}
      onChange={(e: DropdownChangeEvent) => options.editorCallback!(e.value)}
      placeholder="Select"
      style={{ width: "95%" }}
    />
  );


  // Category Dropdown Editor - Only enabled when editing
const categoryEditor = (options: any) => {
  const isEditing = editingRows[options.rowIndex];
  
  return (
    <Dropdown
      value={options.value}
      options={categories}
      onChange={(e: DropdownChangeEvent) => options.editorCallback!(e.value)}
      placeholder="Select Category"
      filter
      filterMatchMode="contains"
      showFilterClear
      filterPlaceholder="Search categories..."
      disabled={!isEditing}
      style={{ 
        width: "95%", 
        backgroundColor: isEditing ? "white" : "#f8f9fa",
        cursor: isEditing ? "pointer" : "not-allowed"
      }}
    />
  );
};


// Handle row edit start
const onRowEditInit = (event: any) => {
  setEditingRows(prev => ({
    ...prev,
    [event.index]: true
  }));
};

// Handle row edit cancel
const onRowEditCancel = (event: any) => {
  setEditingRows(prev => {
    const newState = { ...prev };
    delete newState[event.index];
    return newState;
  });
};

  const onRowEditComplete = async (event: DataTableRowEditCompleteEvent) => {
    const { newData, index } = event;
    const updated = [...materials];
    updated[index] = newData;
    setMaterials(updated);
    setRawMaterials(updated);

    const payload = {
      id: newData.id,
      name: newData.name,
      rmCategoryID: newData.rmCategoryId ?? 0,
      measurementTypeID: newData.measurementTypeId ?? 0,
      hsnCode: newData.hsnCode ?? "",
    };

    try {
      const response = await RawMaterialService.updateRawMaterial(payload);
      if (response) {
        showMessage("Update Raw Material", "Updated successfully!", "success");
      } else {
        showMessage("Update Raw Material", "Failed to update", "error");
      }
    } catch (error) {
      console.error("Error updating raw material:", error);
      showMessage("Update Raw Material", "Error occurred", "error");
    }
  };

  const confirmDelete = (rowData: any) => {
    confirmDialog({
      message: "Are you sure you want to delete this raw material?",
      header: "Confirm Delete",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => handleDelete(rowData),
    });
  };


  const measurementTypeBodyTemplate = (rowData: any) => {
  const measurementType = measurementTypes.find(mt => mt.value === rowData.measurementTypeId);
  return measurementType ? measurementType.label : rowData.measurementTypeName;
};


// Measurement Type Dropdown Editor - Only enabled when editing
const measurementTypeEditor = (options: any) => {
  const isEditing = editingRows[options.rowIndex];
  
  return (
    <Dropdown
      value={options.value}
      options={measurementTypes}
      onChange={(e: DropdownChangeEvent) => options.editorCallback!(e.value)}
      placeholder="Select Measurement"
      filter
      filterMatchMode="contains"
      showFilterClear
      filterPlaceholder="Search measurement types..."
      disabled={!isEditing}
      style={{ 
        width: "95%", 
        backgroundColor: isEditing ? "white" : "#f8f9fa",
        cursor: isEditing ? "pointer" : "not-allowed",
        boxShadow: "0 0 0 0.1rem rgba(155, 177, 143, 0.25)"
      }}
    />
  );
};


  const categoryBodyTemplate = (rowData: any) => {
  const category = categories.find(cat => cat.value === rowData.rmCategoryId);
  return category ? category.label : rowData.rmCategoryName;
};

  const handleDelete = async (rowData: any) => {
    try {
      const response = await RawMaterialService.deleteRawMaterial(rowData.id);
      if (response) {
        const updated = materials.filter((m) => m.id !== rowData.id);
        setMaterials(updated);
        setRawMaterials(updated);
        showMessage("Delete Raw Material", "Deleted successfully!", "success");
      } else {
        showMessage("Delete Raw Material", "Failed to delete", "error");
      }
    } catch (error) {
      console.error("Error deleting raw material:", error);
      showMessage("Delete Raw Material", "Error occurred", "error");
    }
  };

  return (
    <div className="col-12">
      <Toast ref={toast} />
      <ConfirmDialog />
      <div className="card border-noround p-2">
        <DataTable
          value={materials}
          paginator
          rows={10}
          totalRecords={materials.length}
          stripedRows
          editMode="row"
           onRowEditInit={onRowEditInit}  // Add this
          onRowEditCancel={onRowEditCancel} 
          onRowEditComplete={onRowEditComplete}
          paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
          currentPageReportTemplate="{first} to {last} of {totalRecords}"
          sortMode="multiple" 
        >
          <Column field="name" header="Name" editor={textEditor} sortable />
          <Column
         
            field="rmCategoryId"
            header="Category"
            body={categoryBodyTemplate}
             editor={categoryEditor} 
            sortable
          />
          {/* <Column
            field="measurementTypeName"
            header="Measurement Type"
           
            
            sortable
          /> */}


          <Column
            field="measurementTypeId"
            header="Measurement Type"
            editor={(options) => dropdownEditor(options, measurementTypes)}
            body={(rowData) => {
              const type = measurementTypes.find((m) => m.value === rowData.measurementTypeId);
              return type ? type.label : rowData.measurementTypeName || "-";
            }}
            ></Column>
          <Column field="hsnCode" header="HSN Code" editor={textEditor} sortable />
          <Column header="Action" rowEditor style={{ width: "8%" }} />
          <Column
            header="Delete"
            body={(rowData) => (
              <Button
                icon="pi pi-trash"
                onClick={() => confirmDelete(rowData)}
                className="p-button-rounded p-button-text p-button-danger"
              />
            )}
          />
        </DataTable>
      </div>
    </div>
  );
};

export default RawMaterialDatatable;
